import { createContext, useContext, useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

// Tabs minimal — implementación local sin Radix para reducir bundle.
// La interacción es por click; la navegación por teclado es la default del browser
// (los botones reciben foco). En slice 4+ podemos migrar a @radix-ui/react-tabs
// si necesitamos navegación con flechas o aria roles más estrictos.

interface TabsContext {
  value: string;
  onChange: (value: string) => void;
  baseId: string;
}

const Ctx = createContext<TabsContext | null>(null);

function useTabsCtx(): TabsContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('Tabs.* debe usarse dentro de <Tabs>');
  return ctx;
}

interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ value, onChange, children, className }: TabsProps): JSX.Element {
  const baseId = useId();
  return (
    <Ctx.Provider value={{ value, onChange, baseId }}>
      <div className={className}>{children}</div>
    </Ctx.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className }: TabsListProps): JSX.Element {
  return (
    <div role="tablist" className={cn('flex gap-1 border-b border-marca-neutro', className)}>
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
}

export function TabsTrigger({ value, children }: TabsTriggerProps): JSX.Element {
  const { value: current, onChange, baseId } = useTabsCtx();
  const isActive = current === value;
  return (
    <button
      role="tab"
      type="button"
      id={`${baseId}-trigger-${value}`}
      aria-selected={isActive}
      aria-controls={`${baseId}-panel-${value}`}
      onClick={() => onChange(value)}
      className={cn(
        '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-marca-primario',
        isActive
          ? 'border-marca-primario text-text-marca'
          : 'border-transparent text-marca-gris/70 hover:text-marca-gris',
      )}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps): JSX.Element | null {
  const { value: current, baseId } = useTabsCtx();
  if (current !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-trigger-${value}`}
      className={cn('mt-4', className)}
    >
      {children}
    </div>
  );
}
